import React from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  StopOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Button } from 'antd';
import type { ChatProposal } from '../../api/agentChat/interface';
import { useEscDossierContext } from '../DossierContext';
import styles from './ProposalCard.module.scss';

// `task_response` is the current effect block; `escalation_response` is kept as
// a legacy alias so historical proposals still render (treated identically).
type ResponseBlock = {
  type: 'task_response' | 'escalation_response';
  id: string;
  title: string;
  intent: 'resolve' | 'defer';
  text: string;
  defer_to: string | null;
};

type EffectsBlock = { type: 'text'; content: string } | ResponseBlock;

function isResponseBlock(block: EffectsBlock): block is ResponseBlock {
  return block.type === 'task_response' || block.type === 'escalation_response';
}

export function parseExpectedEffects(raw: unknown): EffectsBlock[] {
  if (Array.isArray(raw)) {
    return raw as EffectsBlock[];
  }
  if (typeof raw === 'object' && raw !== null && 'type' in raw) {
    return [raw as EffectsBlock];
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed as EffectsBlock[];
      }
      if (parsed && typeof parsed === 'object' && 'type' in parsed) {
        return [parsed as EffectsBlock];
      }
    } catch {
      // fall through to plain text
    }
    return [{ type: 'text', content: raw }];
  }
  return [];
}

interface Props {
  proposal: ChatProposal;
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  submitting?: boolean;
}

const TaskEffectBlock: React.FC<{ block: ResponseBlock }> = ({ block }) => {
  const { onTaskClick } = useEscDossierContext();
  return (
    <div className={styles.escalationEffect}>
      <div className={styles.escalationEffectHeader}>
        <span
          className={clsx(
            styles.intentBadge,
            block.intent === 'resolve'
              ? styles.intentResolve
              : styles.intentDefer
          )}
        >
          {block.intent === 'resolve' ? 'Resolve' : 'Defer'}
        </span>
        <Button
          type="text"
          size="small"
          className={styles.escalationChip}
          onClick={() => onTaskClick(block.id)}
        >
          {block.title}
        </Button>
      </div>
      {block.text && (
        <p className={styles.escalationEffectText}>{block.text}</p>
      )}
      {block.defer_to && (
        <div className={styles.escalationEffectDefer}>
          <ClockCircleOutlined />
          {dayjs(block.defer_to).format('MMM D, YYYY')}
        </div>
      )}
    </div>
  );
};

export const EscalationEffectBody: React.FC<{ raw: unknown }> = ({ raw }) => {
  const blocks = parseExpectedEffects(raw);
  return (
    <>
      {blocks.map((block, i) =>
        isResponseBlock(block) ? (
          <TaskEffectBlock key={i} block={block} />
        ) : (
          <p key={i} className={styles.directiveText}>
            {block.content}
          </p>
        )
      )}
    </>
  );
};

const ProposalCard: React.FC<Props> = ({ proposal, submitting }) => {
  const { directive, status, expected_effects } = proposal;

  const effectsBody = expected_effects ? (
    <EscalationEffectBody raw={expected_effects} />
  ) : directive ? (
    <p className={styles.directiveText}>{directive}</p>
  ) : null;

  if (status === 'approved' || status === 'executing') {
    return (
      <div className={clsx(styles.card, styles.cardApproved)}>
        <div className={styles.statusRow}>
          <LoadingOutlined className={styles.statusIconSpinner} />
          <span className={styles.statusLabelSpinner}>
            {status === 'approved' ? 'Queued…' : 'Running…'}
          </span>
        </div>
        {effectsBody}
      </div>
    );
  }

  if (status === 'executed') {
    return (
      <div className={clsx(styles.card, styles.cardExecuted)}>
        <div className={styles.statusRow}>
          <CheckCircleOutlined className={styles.statusIconExecuted} />
          <span className={styles.statusLabelExecuted}>Approved</span>
        </div>
        {effectsBody}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className={clsx(styles.card, styles.cardFailed)}>
        <div className={styles.statusRow}>
          <CloseCircleOutlined className={styles.statusIconFailed} />
          <span className={styles.statusLabelFailed}>Failed</span>
        </div>
        {proposal.error && <p className={styles.errorText}>{proposal.error}</p>}
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className={clsx(styles.card, styles.cardRejected)}>
        <div className={styles.statusRow}>
          <StopOutlined className={styles.statusIconRejected} />
          <span className={styles.statusLabelRejected}>Rejected</span>
        </div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className={clsx(styles.card, styles.cardRejected)}>
        <div className={styles.statusRow}>
          <StopOutlined className={styles.statusIconRejected} />
          <span className={styles.statusLabelRejected}>Cancelled</span>
        </div>
      </div>
    );
  }

  // Suppress unused-var warning for `submitting` until it's wired up here
  void submitting;

  return null;
};

export default ProposalCard;
